import type { QueryClient } from "@tanstack/react-query";
import { queryOptions, skipToken, useQuery } from "@tanstack/react-query";
import api from "../api";
import urls from "../urls";
import type { MeteringOrMarketLocation } from "../utils/backend-types";
import { COMPONENT_QUERY_KEY } from "../utils/constants";
import type { ObjectName } from "../utils/enums";

const LIST_QUERY_KEY = "metering-or-market-locations";

export const meteringOrMarketLocationQueries = {
  all: () => [LIST_QUERY_KEY] as const,
  list: (params: { siteId?: number; variantId?: number }) =>
    queryOptions<Array<MeteringOrMarketLocation>>({
      queryKey: [
        ...meteringOrMarketLocationQueries.all(),
        { siteId: params.siteId, variantId: params.variantId }
      ],
      queryFn:
        params.siteId || params.variantId
          ? () =>
              fetchMeteringOrMarketLocations(params.siteId, params.variantId)
          : skipToken
    }),
  detail: (
    objectName: ObjectName.MeteringLocation | ObjectName.MarketLocation,
    id?: number
  ) =>
    queryOptions({
      queryKey: [
        COMPONENT_QUERY_KEY[objectName],
        { meteringOrMarketLocationId: id }
      ],
      queryFn:
        typeof id !== "undefined"
          ? () => fetchMeteringOrMarketLocation(id)
          : skipToken
    })
};

export function useMeteringOrMarketLocation(
  objectName: ObjectName.MeteringLocation | ObjectName.MarketLocation,
  id?: number
) {
  return useQuery(meteringOrMarketLocationQueries.detail(objectName, id));
}

async function fetchMeteringOrMarketLocation(id: number) {
  const response = await api.get<MeteringOrMarketLocation>(
    urls.api.meteringOrMarketLocation(id)
  );
  return response.data;
}

function mergeMeteringOrMarketLocationIntoListCache(
  queryClient: QueryClient,
  params: { siteId?: number; variantId?: number },
  location: MeteringOrMarketLocation
) {
  queryClient.setQueryData(
    meteringOrMarketLocationQueries.list(params).queryKey,
    (old: Array<MeteringOrMarketLocation> | undefined) =>
      old ? [...old, location] : old
  );
}

function updateMeteringOrMarketLocationInListCache(
  queryClient: QueryClient,
  params: { siteId?: number; variantId?: number },
  location: MeteringOrMarketLocation
) {
  queryClient.setQueryData(
    meteringOrMarketLocationQueries.list(params).queryKey,
    (old: Array<MeteringOrMarketLocation> | undefined) =>
      old?.map((cached) => (cached.id === location.id ? location : cached))
  );
}

async function fetchMeteringOrMarketLocations(
  siteId?: number,
  variantId?: number
) {
  const response = await api.get<Array<MeteringOrMarketLocation>>(
    urls.api.meteringOrMarketLocations(siteId, variantId)
  );
  return response.data;
}

export {
  mergeMeteringOrMarketLocationIntoListCache,
  updateMeteringOrMarketLocationInListCache
};
